import { msg } from '@lingui/core/macro';
import { useAuth } from 'modules/auth/ui/hooks/use-auth';
import { mapPayloadToUpsertInput } from 'modules/organizer/domain/data-mappers/organizer-upsert.data-mapper';
import { Organizer } from 'modules/organizer/domain/interfaces/organizer';
import { OrganizerUpsertPayload } from 'modules/organizer/domain/interfaces/organizer-upsert-payload';
import { upsertOrganizer } from 'modules/organizer/store/organizer/actions';
import { useUpdateTournamentTimezoneMutation } from 'modules/championship/store/championship-media-api';
import { useRefetchOrganizer } from 'modules/organizer/ui/hooks/use-refetch-organizer';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { toast } from 'react-toastify';
import { Background, backgroundColors } from 'shared/constants/backgrounds';
import { useFetchLogoBackground } from 'shared/hooks/fetchering/use-fetch-logo-background';
import { useUpdateLogoBackground } from 'shared/hooks/mutations/use-update-logo-background';
import { deriveAsyncState } from 'shared/utils/async-state';
import { useAppDispatch } from 'store/hooks';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

type ProfileFormValues = OrganizerUpsertPayload & { backgroundColor?: Background };

export function useProfileForm() {
  const dispatch = useAppDispatch();
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const { isLoading: loading, info: loadedInfo, isSaving, error, loadedTournamentId } = useOrganizer();
  const { data: logoBackground, isFetching: isLogoBackgroundFetching } = useFetchLogoBackground();
  const { hasTournaments, userTournamentId } = useAuth();
  const { updateLogoBackground } = useUpdateLogoBackground();
  const refetch = useRefetchOrganizer();
  const [updateTimezone] = useUpdateTournamentTimezoneMutation();

  /**
   * Правим только активную организацию. Данные другой в сторе — не «почти то же самое»:
   * форма ушла бы в `upsertTournament` с её `id` и перезаписала бы её данные. Слайс такие
   * данные и так чистит (`fetchOrganizer.pending` / `rejected`), но вкладка им не доверяет:
   * цена ошибки — порча чужой организации.
   */
  const info = loadedInfo && loadedInfo.id === userTournamentId ? loadedInfo : null;

  const freshValues = useMemo((): ProfileFormValues | undefined => {
    if (!info) return undefined;
    return {
      id: info.id,
      name: info.name,
      sportType: info.sportType,
      email: info.email,
      phone: info.phone,
      description: info.description,
      logoUrl: info.logoUrl,
      address: info.address,
      timezone: info.timezone,
      backgroundColor: backgroundColors[logoBackground?.type ?? 0],
    };
  }, [info, logoBackground]);

  /**
   * Снимок исходных значений на время сохранения. Фон логотипа кладётся в кэш, как только ответит Supabase, —
   * обычно раньше `upsertTournament`. Новые `freshValues` со старым организатором и новым фоном форма с
   * `enableReinitialize` приняла бы за сброс: поля откатились бы к старым, а замок «Сохранить» снялся бы посреди
   * запроса. Снимок держится, пока организатор тот же: успех меняет `info` и отпускает снимок, отказ — нет,
   * и правки остаются в форме, чтобы повторить (IMGM-1561).
   */
  const [frozen, setFrozen] = useState<{ info: Organizer; values: ProfileFormValues } | null>(null);
  const initialValues = frozen && frozen.info === info ? frozen.values : freshValues;

  const isLoading = loading || isLogoBackgroundFetching;

  /**
   * Вкладка только правит организацию, поэтому «нечего показать» здесь — не одно
   * состояние, а два разных: организации у пользователя нет вовсе (`empty`, ведём на
   * страницу создания) либо она есть, но не загрузилась (`error`, даём «Повторить»).
   * Ответ `null` при `hasTournaments` — тоже сбой загрузки: правки без `id` не бывает.
   */
  const state = useMemo(
    () =>
      deriveAsyncState({
        itemsCount: info ? 1 : 0,
        hasCurrentData: Boolean(info) || !hasTournaments,
        isFetching: isLoading,
        isPending: hasTournaments && loadedTournamentId === null,
        isError: hasTournaments && !info && (Boolean(error) || loadedTournamentId !== null),
      }),
    [info, hasTournaments, isLoading, loadedTournamentId, error],
  );

  async function handleSubmit(data: OrganizerUpsertPayload) {
    // Без `id` бэкенд трактует `upsertTournament` как создание — именно так вкладка
    // «Профиль» однажды завела пользователю вторую организацию.
    if (!info?.id) {
      toast.error(t`Данные организации не загружены — сохранить изменения нельзя`);
      return;
    }

    if (initialValues) setFrozen({ info, values: initialValues });

    // Владелец — нынешний, не сохраняющий: `upsertTournament` перезаписывает его тем, что пришло (IMGM-1593).
    const params = mapPayloadToUpsertInput({ ...data, id: info.id }, info.adminUserId);
    const bgColor = data.logo?.[0]?.background;
    const timezone = data.timezone;

    // Ждём все три запроса: `isSubmitting` формы — и замок «Сохранить» — снимается, когда отработали и часовой пояс,
    // и фон логотипа, а не только сам организатор (раньше кнопка отпиралась по `isSaving` одного `upsertOrganizer`).
    const [organizerResult, timezoneResult] = await Promise.allSettled([
      dispatch(upsertOrganizer(params)).unwrap(),
      timezone ? updateTimezone({ tournamentId: info.id, timezone }).unwrap() : Promise.resolve(null),
      updateLogoBackground({ bgColor, tournamentId: info.id }),
    ]);

    if (organizerResult.status === 'fulfilled' && timezoneResult.status === 'fulfilled') {
      toast.success(t`Данные сохранены`);
    }
    // Обе ветки независимы: отказали обе — обе причины видны; одна прошла — пользователь знает, что сохранилось.
    if (organizerResult.status === 'rejected') {
      // Причину больше не угадываем («возможно, такое название уже есть») — её даёт реестр по коду.
      toastApiError(organizerResult.reason, msg`Не удалось сохранить данные организатора`);
    }
    if (timezoneResult.status === 'rejected') {
      toastApiError(
        timezoneResult.reason,
        organizerResult.status === 'fulfilled'
          ? msg`Данные организатора сохранены, но не удалось сохранить часовой пояс`
          : msg`Не удалось сохранить часовой пояс`,
      );
    }
  }

  return {
    initialValues,
    isLoading,
    isSaving,
    state,
    error,
    refetch,
    handleSubmit,
  };
}
